/**
 * PageExploreOverlay — the free-explore ("rollover") help mode.
 *
 * Where PageTourOverlay walks the page one spotlight at a time, this shows
 * EVERY section of the current page at once and pairs them with a section
 * index rail: hover or click a section (on the page or in the rail) to
 * spotlight it + read what it is and how to use it well. Clicking a rail item
 * auto-scrolls that section into view. Anyone can learn the whole page in any
 * order, including sections below the fold.
 *
 * Reuses the same `helpKey -> steps` registry (pageTours) + `data-tour`
 * anchors as the guided tour. Launched from the help button via
 * pageTourStore.startExplore(). #113 Mod 2 (Phase 1 + rail/polish redesign).
 */

import { useCallback, useEffect, useLayoutEffect, useState } from 'react'
import { Compass, X, BookOpen, ChevronDown, MousePointerClick, Lightbulb, Play, Search, MessageCircleQuestion } from 'lucide-react'
import { usePageTourStore } from './pageTourStore'
import { getPageTour } from './pageTours'
import { getHelp } from '../helpContent'
import { HelpText } from '../HelpText'
import { TILE_PALETTE } from '../HowThisWorks'
import { cn } from '@/lib/utils'

interface Rect { top: number; left: number; width: number; height: number }
const PAD = 8
const CARD_W = 340
const RAIL_W = 344 // w-[344px] — widened for the answer-first rail (Help v2)

function labelFor(tourKey: string): string {
  return tourKey
    .replace(/^ts-/, '')
    .split('-')
    .filter(Boolean)
    .map((w) => w.charAt(0).toUpperCase() + w.slice(1))
    .join(' ')
}

export function PageExploreOverlay() {
  const activeTourKey = usePageTourStore((s) => s.activeTourKey)
  const mode = usePageTourStore((s) => s.mode)
  const exit = usePageTourStore((s) => s.exit)
  // "Play tour" hands the SAME sections to the sequential stepper
  // (PageTourOverlay) — first-time users get walked in order; Explore stays
  // the reference mode you can hop back to from the stepper.
  const startTour = usePageTourStore((s) => s.start)

  const [rects, setRects] = useState<(Rect | null)[]>([])
  const [hovered, setHovered] = useState<number | null>(null)
  const [pinned, setPinned] = useState<number | null>(null)
  const [aboutOpen, setAboutOpen] = useState(true)
  // "What you can do" + "Pro tips" collapse by default — the overview answers
  // "what is this", and these expand on demand so the rail isn't a wall of text.
  const [whatOpen, setWhatOpen] = useState(false)
  const [tipsOpen, setTipsOpen] = useState(false)
  // Help v2: rail search filters sections + questions + actions + tips live;
  // question accordion tracks its open item.
  const [query, setQuery] = useState('')
  const [openQ, setOpenQ] = useState<number | null>(null)

  const active = !!activeTourKey && mode === 'explore'
  const tour = activeTourKey ? getPageTour(activeTourKey) : null
  // The "What is this page?" summary lives here in the rail — Explore is the
  // core help surface, the read summary sits on top of the section index.
  const help = activeTourKey ? getHelp(activeTourKey) : undefined
  const overviewParas = help ? (Array.isArray(help.overview) ? help.overview : [help.overview]) : []

  const measure = useCallback(() => {
    if (!tour) { setRects([]); return }
    setRects(
      tour.map((s) => {
        if (!s.selector) return null
        const el = document.querySelector(s.selector)
        if (!el) return null
        const r = el.getBoundingClientRect()
        if (r.width === 0 && r.height === 0) return null
        return { top: r.top, left: r.left, width: r.width, height: r.height }
      }),
    )
  }, [tour])

  useLayoutEffect(() => {
    if (!active) return
    // rAF-throttle: collapse a burst of scroll/resize events into one
    // measurement per frame so fast scrolling stays smooth (no layout thrash).
    let raf = 0
    const onChange = () => {
      if (raf) return
      raf = requestAnimationFrame(() => { raf = 0; measure() })
    }
    measure()
    window.addEventListener('scroll', onChange, true)
    window.addEventListener('resize', onChange)
    // The shell reflows <main> (adds right padding) to make room for this rail;
    // window 'resize' doesn't fire for that, so observe <main> directly and
    // re-measure as it animates so the hotspots track the shifting sections.
    const mainEl = document.querySelector('main')
    const ro = mainEl ? new ResizeObserver(onChange) : null
    if (mainEl && ro) ro.observe(mainEl)
    return () => {
      if (raf) cancelAnimationFrame(raf)
      window.removeEventListener('scroll', onChange, true)
      window.removeEventListener('resize', onChange)
      ro?.disconnect()
    }
  }, [active, measure])

  useEffect(() => {
    // Fresh start each time explore opens.
    setHovered(null)
    setPinned(null)
    setQuery('')
    setOpenQ(null)
  }, [activeTourKey, mode])

  useEffect(() => {
    if (!active) return
    const onKey = (e: KeyboardEvent) => { if (e.key === 'Escape') exit() }
    window.addEventListener('keydown', onKey)
    return () => window.removeEventListener('keydown', onKey)
  }, [active, exit])

  const pin = useCallback((i: number) => {
    setPinned(i)
    const sel = tour?.[i]?.selector
    if (sel) document.querySelector(sel)?.scrollIntoView({ block: 'center', behavior: 'smooth' })
  }, [tour])

  if (!active || !tour) return null

  const focus = hovered ?? pinned
  const focusStep = focus != null ? tour[focus] : null
  const focusRect = focus != null ? rects[focus] : null
  const resolvedCount = rects.filter(Boolean).length
  const pageLabel = labelFor(activeTourKey)
  // Sequential 1-based number among PRESENT sections only, so the on-page
  // badges and the rail read 1..N with no gaps — absent sections (anchor not
  // rendered for this state/data) are omitted entirely, not shown as skipped.
  let _seq = 0
  const displayNum = rects.map((r) => (r ? ++_seq : 0))

  // Help v2 — one search needle filters sections, questions, actions, and
  // tips. Filtering only hides RAIL items; the on-page badges keep their
  // stable numbering (a filtered rail never renumbers the page).
  const needle = query.trim().toLowerCase()
  const hasNeedle = needle.length > 0
  const matchesQ = (...parts: Array<string | undefined>) =>
    !hasNeedle || parts.filter(Boolean).join(' ').toLowerCase().includes(needle)
  const questions = help?.questions ?? []
  const visQuestions = questions
    .map((item, qi) => ({ item, qi }))
    .filter(({ item }) => matchesQ(item.q, item.a))
  const visWhat = (help?.whatYouCanDo ?? []).filter((it) => matchesQ(it))
  const visTips = (help?.tips ?? []).filter((it) => matchesQ(it))
  const aboutMatches = matchesQ(...overviewParas)
  const sectionVisible = tour.map((s, i) => !!rects[i] && matchesQ(s.title, s.body, ...(s.points ?? [])))
  const visSectionCount = sectionVisible.filter(Boolean).length
  const nothingMatches =
    hasNeedle && visSectionCount === 0 && visQuestions.length === 0 &&
    visWhat.length === 0 && visTips.length === 0 && !aboutMatches

  // Callout position: beside the focused hotspot (below if room, else above),
  // clamped to the area left of the rail. Centered there if the section is
  // off-screen (focused via the rail but not currently rendered).
  let cardStyle: React.CSSProperties = { top: 90, left: Math.max(12, (window.innerWidth - RAIL_W) / 2 - CARD_W / 2) }
  let placedAbove = false
  if (focusRect) {
    const below = focusRect.top + focusRect.height + PAD + 10
    placedAbove = below + 180 > window.innerHeight
    const left = Math.max(12, Math.min(focusRect.left, window.innerWidth - RAIL_W - CARD_W - 12))
    cardStyle = placedAbove
      ? { top: Math.max(12, focusRect.top - PAD - 10 - 180), left }
      : { top: below, left }
  }

  // Connector line from the focused rail item (right) to its section (page).
  let connector: { x1: number; y1: number; x2: number; y2: number } | null = null
  if (focus != null && focusRect) {
    const rr = document.querySelector(`[data-rail-idx="${focus}"]`)?.getBoundingClientRect()
    if (rr) {
      connector = {
        x1: rr.left,
        y1: rr.top + rr.height / 2,
        x2: focusRect.left + focusRect.width + PAD,
        y2: focusRect.top + focusRect.height / 2,
      }
    }
  }

  // pointer-events-none on the root so wheel/scroll passes THROUGH to the page
  // (you can scroll to reach sections below the fold). Only the hotspots + rail
  // re-enable pointer events; hotspots track the page via the scroll listener.
  return (
    <div className="fixed inset-0 z-[60] pointer-events-none" role="dialog" aria-label={`Explore ${pageLabel}`}>
      {/* Dim layer: a light tint in overview; a real spotlight cut-out around
          the focused section (the rest of the page darkens) when one is focused
          via a big box-shadow on the focused rect. */}
      {focus == null && <div className="absolute inset-0 bg-slate-900/10" />}
      {focus != null && !focusRect && <div className="absolute inset-0 bg-slate-900/30" />}
      {focus != null && focusRect && (
        <div
          className="absolute rounded-lg transition-[box-shadow] duration-200"
          style={{
            top: focusRect.top - PAD,
            left: focusRect.left - PAD,
            width: focusRect.width + PAD * 2,
            height: focusRect.height + PAD * 2,
            boxShadow: '0 0 0 9999px rgba(15, 23, 42, 0.35)',
          }}
        />
      )}

      {/* Hotspots — every section at once. */}
      {rects.map((r, i) => {
        if (!r) return null
        const on = focus === i
        return (
          <div
            key={i}
            className={cn(
              // pointer-events-none so wheel/scroll passes straight through to the
              // page — you can scroll from anywhere. Only the small badge below
              // (re-enabled) is interactive.
              'pointer-events-none absolute rounded-lg ring-2 transition-[transform,background-color,box-shadow] duration-150',
              on ? 'ring-brand-500 bg-brand-400/10 scale-[1.01]' : 'ring-brand-300/70',
            )}
            style={{ top: r.top - PAD, left: r.left - PAD, width: r.width + PAD * 2, height: r.height + PAD * 2 }}
          >
            <button
              type="button"
              onMouseEnter={() => setHovered(i)}
              onMouseLeave={() => setHovered((h) => (h === i ? null : h))}
              onClick={() => pin(i)}
              aria-label={`Section ${i + 1}`}
              className={cn(
                'pointer-events-auto cursor-pointer absolute -top-2.5 -left-2.5 h-6 w-6 inline-flex items-center justify-center rounded-full text-white text-[11px] font-bold shadow ring-2 ring-white transition-colors',
                on ? 'bg-brand-600' : 'bg-brand-500 hover:bg-brand-600',
                focus === null && 'animate-pulse',
              )}
            >
              {displayNum[i]}
            </button>
          </div>
        )
      })}

      {/* Connector line from the rail item to its section. */}
      {connector && (
        <svg className="absolute inset-0 h-full w-full pointer-events-none" style={{ overflow: 'visible' }}>
          <line
            x1={connector.x2} y1={connector.y2} x2={connector.x1} y2={connector.y1}
            stroke="#6081ad" strokeWidth={2} strokeDasharray="5 4" strokeLinecap="round"
          />
          <circle cx={connector.x2} cy={connector.y2} r={3.5} fill="#6081ad" />
        </svg>
      )}

      {/* Callout for the focused section. */}
      {focusStep && (
        <div
          className="absolute w-[340px] max-w-[calc(100vw-32px)] bg-white/45 backdrop-blur-xl rounded-xl shadow-2xl ring-1 ring-black/5 border border-white/50 overflow-hidden pointer-events-none"
          style={cardStyle}
        >
          {focusRect && (
            <div
              className={cn('absolute h-3 w-3 rotate-45', placedAbove ? 'bg-white border-r border-b border-slate-200' : 'bg-brand-600')}
              style={placedAbove ? { left: 22, bottom: -6 } : { left: 22, top: -6 }}
            />
          )}
          <div className="px-4 py-2.5 bg-gradient-to-r from-brand-600 to-indigo-600 text-white flex items-center gap-2">
            <span className="h-5 w-5 shrink-0 inline-flex items-center justify-center rounded-full bg-white/20 text-[11px] font-bold">{focus != null ? displayNum[focus] : 1}</span>
            <h3 className="text-sm font-semibold">{focusStep.title}</h3>
          </div>
          <div className="px-4 py-3 space-y-2">
            <p className="text-[13px] font-medium text-slate-800 leading-snug">{focusStep.body}</p>
            {focusStep.points && focusStep.points.length > 0 && (
              <ul className="space-y-1">
                {focusStep.points.map((pt, k) => (
                  <li key={k} className="flex items-start gap-2 text-[12px] text-slate-600 leading-snug">
                    <span className="mt-[5px] h-1 w-1 rounded-full bg-brand-500 shrink-0" />
                    <span>{pt}</span>
                  </li>
                ))}
              </ul>
            )}
          </div>
        </div>
      )}

      {/* Section index rail. */}
      <div className="pointer-events-auto absolute right-0 top-0 bottom-0 w-[344px] bg-white/35 backdrop-blur-xl border-l border-white/40 shadow-2xl flex flex-col">
        <div className="px-4 py-3 bg-gradient-to-r from-brand-600 to-indigo-600 text-white flex items-center justify-between">
          <div className="min-w-0">
            <div className="text-[10px] uppercase tracking-wider text-white/70 inline-flex items-center gap-1">
              <Compass className="w-3 h-3" /> Explore
            </div>
            <div className="text-sm font-semibold truncate">{pageLabel}</div>
          </div>
          <button
            onClick={exit}
            className="inline-flex items-center gap-1 px-2 py-1 rounded-md bg-white/15 hover:bg-white/25 text-white text-xs font-medium transition-colors shrink-0"
            aria-label="Exit explore"
          >
            <X className="w-3.5 h-3.5" /> Done
          </button>
        </div>
        <div className="px-3 py-2 border-b border-slate-100 flex items-center gap-2">
          <button
            type="button"
            onClick={() => startTour(activeTourKey)}
            className="inline-flex items-center gap-1.5 px-2.5 py-1.5 rounded-lg bg-brand-600 hover:bg-brand-700 active:scale-95 text-white text-[11px] font-semibold transition-colors shrink-0"
            title="Walk the sections one at a time with Next/Back"
          >
            <Play className="w-3 h-3" /> Play tour
          </button>
          <span className="text-[11px] text-slate-500 leading-tight">
            {resolvedCount} section{resolvedCount === 1 ? '' : 's'} · hover or click to learn each · Esc exits
          </span>
        </div>
        {/* Rail search — filters sections, questions, actions, and tips (Help v2). */}
        <div className="px-2.5 py-2 border-b border-slate-100">
          <label className="flex items-center gap-2 rounded-lg border border-slate-200 bg-white/70 px-2.5 py-1.5 transition-colors focus-within:border-brand-400">
            <Search className="w-3.5 h-3.5 text-slate-400 shrink-0" />
            <input
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              placeholder="Search this page's help…"
              aria-label="Search this page's help"
              className="w-full bg-transparent text-[12px] text-slate-800 outline-none placeholder:text-slate-400"
            />
            {query && (
              <button
                type="button"
                onClick={() => setQuery('')}
                aria-label="Clear search"
                className="text-slate-400 hover:text-slate-600"
              >
                <X className="w-3 h-3" />
              </button>
            )}
          </label>
        </div>
        <div className="flex-1 overflow-y-auto py-1">
          {/* "What is this page?" summary — Explore is the core surface, so the
              read summary sits on top of the section index in the same window.
              Collapsible; hidden while a search filters it out. */}
          {help && (!hasNeedle || aboutMatches) && (
            <div className="mx-2 mb-1.5 rounded-lg border border-white/50 bg-white/55 overflow-hidden">
              <button
                onClick={() => setAboutOpen((v) => !v)}
                aria-expanded={aboutOpen}
                className="flex w-full items-center gap-1.5 px-3 py-2 text-left text-[12px] font-semibold text-slate-800 hover:bg-white/40 transition-colors"
              >
                <BookOpen className="w-3.5 h-3.5 text-brand-600 shrink-0" />
                What is this page?
                <ChevronDown className={cn('ml-auto w-3.5 h-3.5 text-slate-400 transition-transform', !aboutOpen && '-rotate-90')} />
              </button>
              {aboutOpen && (
                <div className="px-3 pb-3 pt-0.5 space-y-2.5">
                  {overviewParas.map((p, i) => (
                    <p key={i} className="text-[12px] text-slate-600 leading-snug">
                      <HelpText text={p} onNavigate={exit} />
                    </p>
                  ))}
                </div>
              )}
            </div>
          )}

          {/* Section tiles — the modal's color language in the rail. Hovering
              or clicking spotlights that section on the live page (Show me). */}
          {visSectionCount > 0 && (
            <div className="px-3 pb-1 pt-1 text-[10px] uppercase tracking-wider font-semibold text-slate-400">
              Sections on this page
            </div>
          )}
          <div className="px-2 space-y-1.5">
            {tour.map((s, i) => {
              // Only sections actually rendered on this view get a rail item;
              // absent ones are omitted (not shown as "skipped").
              if (!sectionVisible[i]) return null
              const on = focus === i
              const tone = TILE_PALETTE[(displayNum[i]! - 1) % TILE_PALETTE.length]!
              return (
                <button
                  key={i}
                  type="button"
                  data-rail-idx={i}
                  onMouseEnter={() => setHovered(i)}
                  onMouseLeave={() => setHovered((h) => (h === i ? null : h))}
                  onClick={() => pin(i)}
                  className={cn(
                    'w-full text-left px-2.5 py-2 rounded-xl border flex items-start gap-2.5 transition-all',
                    tone.bg, tone.border,
                    on ? 'ring-2 ring-brand-400 shadow-sm' : 'hover:shadow-sm',
                  )}
                >
                  <span className={cn('mt-0.5 h-5 w-5 shrink-0 inline-flex items-center justify-center rounded-full text-[10px] font-bold', tone.iconBg, tone.iconText)}>
                    {displayNum[i]}
                  </span>
                  <span className="min-w-0 flex-1">
                    <span className={cn('block text-[12.5px] font-semibold leading-tight', tone.label)}>{s.title}</span>
                    {s.body && <span className="block text-[11px] text-slate-600 leading-snug mt-0.5">{s.body}</span>}
                    <span className={cn('mt-1 inline-flex items-center gap-1 text-[9.5px] font-bold uppercase tracking-wide', tone.iconText, on ? 'opacity-100' : 'opacity-60')}>
                      <MousePointerClick className="h-3 w-3" /> {on ? 'Spotlighted on the page' : 'Show me'}
                    </span>
                  </span>
                </button>
              )
            })}
          </div>

          {/* Common questions — the answer-first core (Help v2). Matched
              questions open automatically while searching. */}
          {visQuestions.length > 0 && (
            <>
              <div className="px-3 pb-1 pt-3 text-[10px] uppercase tracking-wider font-semibold text-slate-400 flex items-center gap-1">
                <MessageCircleQuestion className="w-3 h-3" /> Common questions
              </div>
              <div className="px-2 space-y-1.5">
                {visQuestions.map(({ item, qi }) => {
                  const isOpen = hasNeedle || openQ === qi
                  return (
                    <div key={qi} className={cn('rounded-xl border bg-white/70 transition-colors', isOpen ? 'border-brand-300' : 'border-slate-200 hover:border-brand-200')}>
                      <button
                        type="button"
                        onClick={() => setOpenQ(isOpen && !hasNeedle ? null : qi)}
                        aria-expanded={isOpen}
                        className="flex w-full items-start gap-2 px-2.5 py-2 text-left"
                      >
                        <span className="inline-flex h-5 w-5 items-center justify-center rounded-full bg-brand-100 text-brand-700 text-[10px] font-bold shrink-0">?</span>
                        <span className="text-[12px] font-medium text-slate-800 leading-snug flex-1">{item.q}</span>
                        <ChevronDown className={cn('mt-0.5 w-3.5 h-3.5 shrink-0 text-slate-400 transition-transform', !isOpen && '-rotate-90')} />
                      </button>
                      {isOpen && (
                        <p className="px-2.5 pb-2.5 pl-9 text-[11.5px] text-slate-600 leading-snug">
                          <HelpText text={item.a} onNavigate={exit} />
                        </p>
                      )}
                    </div>
                  )
                })}
              </div>
            </>
          )}

          {/* What you can do / Pro tips — collapsed by default, forced open
              while a search has matches inside them. */}
          {visWhat.length > 0 && (
            <div className="mx-2 mt-3 rounded-md border border-slate-200 bg-white/60 overflow-hidden">
              <button
                onClick={() => setWhatOpen((v) => !v)}
                aria-expanded={whatOpen || hasNeedle}
                className="flex w-full items-center gap-1 px-2.5 py-1.5 text-left text-[10px] uppercase tracking-wider font-semibold text-slate-500 hover:bg-white/60 transition-colors"
              >
                <MousePointerClick className="w-3 h-3" /> What you can do
                <span className="text-slate-400 normal-case">({visWhat.length})</span>
                <ChevronDown className={cn('ml-auto w-3 h-3 text-slate-400 transition-transform', !(whatOpen || hasNeedle) && '-rotate-90')} />
              </button>
              {(whatOpen || hasNeedle) && (
                <ol className="space-y-1 px-2.5 pb-2">
                  {visWhat.map((it, i) => (
                    <li key={i} className="flex items-start gap-1.5 text-[11px] text-slate-600 leading-snug">
                      <span className="inline-flex h-4 w-4 items-center justify-center rounded bg-brand-100 text-brand-700 text-[9px] font-bold tabular-nums shrink-0">{i + 1}</span>
                      <span><HelpText text={it} onNavigate={exit} /></span>
                    </li>
                  ))}
                </ol>
              )}
            </div>
          )}
          {visTips.length > 0 && (
            <div className="mx-2 mt-2 mb-2 rounded-md border border-amber-200 bg-amber-50/70 overflow-hidden">
              <button
                onClick={() => setTipsOpen((v) => !v)}
                aria-expanded={tipsOpen || hasNeedle}
                className="flex w-full items-center gap-1 px-2.5 py-1.5 text-left text-[10px] uppercase tracking-wider font-semibold text-amber-800 hover:bg-amber-100/60 transition-colors"
              >
                <Lightbulb className="w-3 h-3" /> Pro tips
                <span className="text-amber-600 normal-case">({visTips.length})</span>
                <ChevronDown className={cn('ml-auto w-3 h-3 text-amber-600 transition-transform', !(tipsOpen || hasNeedle) && '-rotate-90')} />
              </button>
              {(tipsOpen || hasNeedle) && (
                <ul className="space-y-1 px-2.5 pb-2">
                  {visTips.map((t, i) => (
                    <li key={i} className="flex items-start gap-1.5 text-[11px] text-amber-900 leading-snug">
                      <span className="inline-block h-1 w-1 rounded-full bg-amber-500 mt-1.5 shrink-0" />
                      <span><HelpText text={t} onNavigate={exit} /></span>
                    </li>
                  ))}
                </ul>
              )}
            </div>
          )}

          {nothingMatches && (
            <p className="px-4 py-6 text-center text-[12px] text-slate-500 italic">
              Nothing in this page's help matches "{query}".{' '}
              <button type="button" onClick={() => setQuery('')} className="not-italic font-semibold text-brand-600 hover:underline">
                Clear
              </button>
            </p>
          )}
        </div>
      </div>
    </div>
  )
}
